import React, { useRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@mui/styles';

import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';

import DissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';

import { format as D3format } from 'd3';
import sizeMe from 'react-sizeme';

import { FlatBulletChart } from '../../../components/charts/bulletchart';
import Radar from '../../../components/charts/radar';
import CopyText from '../../../components/copytext';

const useStyles = makeStyles(theme => ({
    root: {
    },
    satisfied: {
        fontSize: 20,
        marginRight: 5,
        color: theme.palette.info.main,
    },
    dissatisfied: {
        fontSize: 20,
        marginRight: 5,
        color: theme.palette.error.main,
    },
    infoPanel: {
        backgroundColor: theme.palette.background.default,
        padding: 10,
        marginTop: 10,
    },
    infoLabel: {
        textTransform: 'uppercase',
        fontSize: 'smaller',
        color: theme.palette.primary.label,
        width: 82,
        maxWidth: 82,
    },
    infoValue: {
        fontSize: 'smaller',
        overflowWrap: 'break-word',
        paddingLeft: 20,
        cursor: 'pointer',
        transition: 'color 0.3s ease-in',
    },
}));

const MINRADAR = 250;
const MININFO = 260;
const LABELS = 230;

const InfoKeyValue = ({ label, value, width }) => {
    const classes = useStyles();

    return (
        <Grid container direction="row" wrap="nowrap">
            <Grid xs={3} item className={classes.infoLabel}>
                {label}
            </Grid>
            <Grid style={{ width: width - 95 }}>
                <CopyText className={classes.infoValue} text={`${value}`}/>
            </Grid>
        </Grid>
    );
};
InfoKeyValue.propTypes = {
    width: PropTypes.number,
    label: PropTypes.string,
    value: PropTypes.string,
};

const MLResults = sizeMe()((props) => {
    const mainRef = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const [radarWidth, setRadarWidth] = useState(MINRADAR);
    const [infoWidth, setInfoWidth] = useState(270);

    useEffect(() => {
        const s = Math.min(Math.max(
            props.height,
            MINRADAR,
        ) + LABELS, props.size.width - MININFO);

        setRadarWidth(s);
        setInfoWidth(props.size.width - s);
    }, [props.height, props.size]);

    return !props.data ? null
        : <div className={clsx(props.className, classes.root)} ref={mainRef}>
            <Grid container justifyContent="flex-start" style={{ width: 'auto' }} spacing={3}>

                {/* the radar */}
                <Grid item style={{ width: radarWidth }}>
                    <Radar
                        heighttt={Math.min(props.height, radarWidth)}
                        height={props.height}
                        width={radarWidth}
                        data={props.data} />
                </Grid>

                {/* the info */}
                <Grid
                    container
                    direction="column"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    style={{ width: 'auto', marginTop: 20 }}
                    wrap="nowrap">

                    {/* score */}
                    <Grid item>
                        <div style= {{ display: 'inline-flex' }}>
                            <span>
                                {+props.data.Score > +props.data.Threshold
                                    ? <DissatisfiedIcon className={classes.dissatisfied}/>
                                    : <SatisfiedAltIcon className={classes.satisfied}/>
                                }
                            </span>
                            score: {D3format(',.0f')(100 * +props.data.Score)}
                        </div>
                        <div style= {{ width: infoWidth, maxWidth: 700 }}>
                            <FlatBulletChart
                                value={100 * props.data.Score}
                                pct={true}
                                threshold={100 * props.data.Threshold}
                                description={`Alert Threshold: ${100 * props.data.Threshold}`}
                                axis={true}
                                color={+props.data.Score > +props.data.Threshold
                                    ? theme.palette.error.main
                                    : theme.palette.info.main
                                }/>
                        </div>
                    </Grid>

                    {/* file info panel */}
                    <Grid item
                        className={classes.infoPanel}
                        style={{ width: infoWidth, maxWidth: 700 }}>
                        <Grid
                            container
                            direction="column"
                            alignItems="flex-start"
                            justifyContent="flex-start">
                            <Grid item>
                                <InfoKeyValue
                                    label="file name"
                                    value={props.data.fn}
                                    width={infoWidth}/>
                                <InfoKeyValue
                                    label="file size"
                                    value={`${props.fileSize}`}
                                    width={infoWidth}/>
                                <InfoKeyValue
                                    label="file type"
                                    value={props.fileDescription}
                                    width={infoWidth}/>
                                <InfoKeyValue
                                    label="md5"
                                    value={props.fileMd5}
                                    width={infoWidth}/>
                                <InfoKeyValue
                                    label="sha256"
                                    value={props.data.SHA256}
                                    width={infoWidth}/>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </div>;
});

MLResults.propTypes = {
    // width provided by sizeme
    size: PropTypes.object,
    // height provided by parent
    height: PropTypes.number,
    data: PropTypes.object,
    className: PropTypes.string,
    fileSize: PropTypes.number,
    fileDescription: PropTypes.string,
    fileMd5: PropTypes.string,
};

export default MLResults;
